import React, { useEffect, useRef, useState } from 'react';
import { Alert, Button, Card, CardBody, Input, Label, Modal, ModalBody, ModalFooter, ModalHeader, Spinner } from 'reactstrap';
import { withTranslation } from 'react-i18next';
import PhishingIcon from '../../../Layout/Components/PhishingIcon';
import PageTitle from '../../../Layout/Components/PageTitle';
import { getEmailTemplateList, getlandingpages } from '../../../API/PhishingAPI';
import { simulationTemplates } from '../../../shared/simulationTemplates';
import LandingPage from '../LandingPage/LandingPage';
import EmailTemplate from '../EmailTemplate/EmailTemplate';
import { TemplatePicker } from '../Simulations/SimulationPickers';
import TemplateImportPicker from '../Simulations/TemplateImportPicker';
import ChoiceDeleteModal from '../Simulations/ChoiceDeleteModal';
import TemplateGallery from './TemplateGallery';
import { createBlankTemplate } from './templateSets';
import '../Simulations/Simulations.css';
import './SimulationTemplates.css';

const emptySets = [];
const upsert = (items, item) => [...items.filter(old => String(old.id) !== String(item.id)), item];

export function SimulationTemplates({ t, location }) {
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [revision, setRevision] = useState(0);
  const [libraryOpen, setLibraryOpen] = useState(false);
  const [componentsOpen, setComponentsOpen] = useState(false);
  const [component, setComponent] = useState('message');
  const [compose, setCompose] = useState(false);
  const [name, setName] = useState('');
  const [pageId, setPageId] = useState('');
  const [messageId, setMessageId] = useState('');
  const [draft, setDraft] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const openedTemplate = useRef('');
  const linkedTemplate = new URLSearchParams(location?.search || '').get('template');
  useEffect(() => {
    if (!data || !linkedTemplate || openedTemplate.current === linkedTemplate) return;
    openedTemplate.current = linkedTemplate;
    const item = data.sets.find(item => String(item.id) === linkedTemplate);
    if (item) setDraft(item);
    else setError(t('template_gallery.linked_missing'));
  }, [data, linkedTemplate, t]);

  useEffect(() => {
    let active = true;
    setError('');
    Promise.all([getlandingpages(), getEmailTemplateList()]).then(([pages, messages]) => {
      if (!Array.isArray(pages) || !Array.isArray(messages)) throw new Error('Invalid templates');
      if (active) setData({ pages, messages, sets: simulationTemplates(pages, messages) });
    }).catch(() => { if (active) setError(t('template_gallery.my_error')); });
    return () => { active = false; };
  }, [revision, t]);

  function refresh() { setRevision(value => value + 1); }
  function imported(item) {
    setDraft(null); setLibraryOpen(false);
    setData(current => {
      const pages = upsert(current?.pages || [], item.page);
      const messages = upsert(current?.messages || [], item.message);
      return { pages, messages, sets: upsert(current?.sets || [], item) };
    });
    refresh();
  }
  function deleted(_kind, item, result) {
    setData(current => ({ ...current,
      sets: current.sets.filter(set => String(set.id) !== String(item.id)),
      messages: current.messages.filter(message => String(message.id) !== String(item.id)),
      pages: result?.page_deleted ? current.pages.filter(page => String(page.id) !== String(result.page_id)) : current.pages
    }));
    setDeleteTarget(null); refresh();
  }
  function edit(item) {
    // A broken local association can be repaired in the same editor. Keep the
    // existing email ID and add a starter page rather than hiding the template.
    const blank = createBlankTemplate(t);
    setDraft({ ...item, page: item.page || blank.page, message: item.message || blank.message,
      fromSingle: !item.page || !item.message });
  }
  function openCompose() {
    setName(''); setPageId(''); setMessageId(''); setLibraryOpen(false); setCompose(true); refresh();
  }
  function assemble() {
    const page = data.pages.find(item => String(item.id) === pageId);
    const message = data.messages.find(item => String(item.id) === messageId);
    if (!name.trim() || !page || !message) return;
    setDraft({ id: 'new-set', name: name.trim(), page, message });
    setCompose(false);
  }
  function closeComponents() { setComponentsOpen(false); refresh(); }

  return <div className="simulation-wizard simulation-templates-page">
    <PageTitle heading={t('template_gallery.menu')} subheading={t('template_gallery.workflow')} icon={<PhishingIcon name="templates" />} />
    <Card className="simulation-wizard__shell"><CardBody>
      <div className="simulation-templates-page__heading">
        <div><h2>{t('template_gallery.tab_mine')}{data && <span>{data.sets.length}</span>}</h2>
          <p>{t('template_gallery.manage_help')}</p></div>
        <Button color="primary" disabled={!data} onClick={() => setLibraryOpen(true)}>+ {t('template_gallery.create_set')}</Button>
      </div>
      {error && <Alert color="danger" role="alert">{error}<Button color="link" onClick={refresh}>{t('template_gallery.retry')}</Button></Alert>}
      {!data ? !error && <div className="simulation-wizard__loading" role="status"><Spinner size="sm" /> {t('simulation_wizard.loading')}</div>
        : <TemplatePicker manage items={data.sets} onSelect={edit} onEdit={edit} onDelete={item => setDeleteTarget({ kind: 'templates', item })}
          onCreate={() => setLibraryOpen(true)} t={t} />}
      <div className="simulation-templates-page__tools">
        <Button color="link" disabled={!data} onClick={openCompose}>{t('template_gallery.combine_existing')}</Button>
        <Button color="link" onClick={() => setComponentsOpen(true)}>{t('template_gallery.manage_components')}</Button>
      </div>
    </CardBody></Card>
    {libraryOpen && <TemplateImportPicker onClose={() => setLibraryOpen(false)} onBlank={() => setDraft(createBlankTemplate(t))}
      onCombine={openCompose} onImported={imported} primaryActionLabel={t('template_gallery.import_template')} t={t} />}
    {draft && <TemplateGallery editorOnly sets={emptySets} draft={draft} onClose={() => setDraft(null)} onImported={imported}
      primaryActionLabel={t(draft.existing ? 'template_gallery.save_changes' : 'template_gallery.save_template')}
      contextHelp={t('simulation_wizard.template_edit_help')} />}
    {deleteTarget && <ChoiceDeleteModal target={deleteTarget} onClose={() => setDeleteTarget(null)} onDeleted={deleted} t={t} />}
    {componentsOpen && <Modal isOpen toggle={closeComponents} size="xl" scrollable className="simulation-components-modal">
      <ModalHeader toggle={closeComponents}>{t('template_gallery.manage_components')}</ModalHeader>
      <ModalBody><p className="text-muted">{t('template_gallery.components_help')}</p>
        <div className="btn-group mb-3" role="group" aria-label={t('template_gallery.tab_components')}>
          <Button color={component === 'message' ? 'primary' : 'light'} aria-pressed={component === 'message'} onClick={() => setComponent('message')}>{t('template_gallery.message')}</Button>
          <Button color={component === 'page' ? 'primary' : 'light'} aria-pressed={component === 'page'} onClick={() => setComponent('page')}>{t('template_gallery.landing')}</Button>
        </div>
        {component === 'page' ? <LandingPage componentsOnly /> : <EmailTemplate componentsOnly />}
      </ModalBody>
      <ModalFooter><Button color="link" onClick={closeComponents}>{t('template_gallery.cancel')}</Button></ModalFooter>
    </Modal>}
    {compose && <Modal isOpen toggle={() => setCompose(false)}>
      <ModalHeader toggle={() => setCompose(false)}>{t('template_gallery.combine_existing')}</ModalHeader>
      <ModalBody>
        <p>{t('template_gallery.compose_help')}</p>
        {error && <Alert color="danger" role="alert">{error}<Button color="link" onClick={refresh}>{t('template_gallery.retry')}</Button></Alert>}
        <Label for="compose-name">{t('template_gallery.name')}</Label>
        <Input id="compose-name" value={name} onChange={event => setName(event.target.value)} />
        <Label for="compose-message" className="mt-3">{t('template_gallery.message')}</Label>
        <Input id="compose-message" type="select" value={messageId} onChange={event => setMessageId(event.target.value)}>
          <option value="">{t('template_gallery.select_message')}</option>
          {data?.messages.map(item => <option key={item.id} value={item.id}>{item.name}</option>)}
        </Input>
        <Label for="compose-page" className="mt-3">{t('template_gallery.landing')}</Label>
        <Input id="compose-page" type="select" value={pageId} onChange={event => setPageId(event.target.value)}>
          <option value="">{t('template_gallery.select_page')}</option>
          {data?.pages.map(item => <option key={item.id} value={item.id}>{item.name}</option>)}
        </Input>
        {(!data?.pages.length || !data?.messages.length) && <Alert color="info" className="mt-3">
          {t('template_gallery.create_components_first')}
          <Button color="link" onClick={() => { setCompose(false); setComponentsOpen(true); }}>{t('template_gallery.tab_components')}</Button>
        </Alert>}
      </ModalBody>
      <ModalFooter>
        <Button color="link" onClick={() => setCompose(false)}>{t('template_gallery.cancel')}</Button>
        <Button color="primary" disabled={!name.trim() || !pageId || !messageId || !!error} onClick={assemble}>{t('template_gallery.continue')}</Button>
      </ModalFooter>
    </Modal>}
  </div>;
}

export default withTranslation()(SimulationTemplates);
